"use client";

import { AudioLines, Mic, Send, Square } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import type { ComposerMode } from "@/app/ai-agent/types";
import StopWaveIcon from "@/assets/images/ai-voice-stop.svg";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export interface AiChatInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Send the current prompt (Enter or the send button). */
  onSend: () => void;
  /** Composer state — drives the labels + controls (see `ComposerMode`). */
  mode: ComposerMode;
  /** Start simple mic dictation (the mic button). */
  onStartDictation: () => void;
  /** Start voice mode (the waveform button) — UI-only until voice-agent ships. */
  onStartVoice: () => void;
  /** Leave any capture mode back to the default `text` mode. */
  onStop: () => void;
  maxChars: number;
  placeholder?: string;
  /** Show the "voice mode" waveform button (text mode, empty). Off in the compact popover. */
  showVoiceButton?: boolean;
  /**
   * A turn is streaming — the send button becomes a "stop generating" button
   * (calls `onCancelTurn`) and Enter is inert. Optional: launcher surfaces (the
   * Ask-AI popover) don't stream, so they omit it.
   */
  isStreaming?: boolean;
  /** Abort the active streaming turn (shown only while `isStreaming`). */
  onCancelTurn?: () => void;
  /** Real voice-session state — drives the Connecting/Listening/Speaking label. */
  voiceStatus?: "idle" | "connecting" | "listening" | "speaking";
}

function IconButton({
  label,
  onClick,
  children,
  className,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        "flex size-8 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-bg-weak-50 hover:text-text-secondary",
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * Compact chat composer — the Figma 213:202356 input shell in every mode:
 * solid white card, neutral-200 border, blurred blue/warm glow bleeding up
 * from the bottom edge, single-line textarea, footer controls.
 * During mic dictation the textarea stays visible so live transcripts can update.
 * Agent voice mode (`connecting`/`voice`) instead collapses to a status caption
 * and swaps the footer for a static mic indicator + waveform "Stop" pill.
 */
export function AiChatInput({
  value,
  onChange,
  onSend,
  mode,
  onStartDictation,
  onStartVoice,
  onStop,
  maxChars,
  placeholder = "Ask Cashless Ai",
  showVoiceButton = true,
  isStreaming = false,
  onCancelTurn,
  voiceStatus,
}: AiChatInputProps) {
  const hasText = value.trim().length > 0;
  const dictating = mode === "dictation";
  const voiceUi = mode === "connecting" || mode === "voice";
  const statusLabel =
    mode === "connecting"
      ? "Connecting…"
      : voiceStatus === "speaking"
        ? "Speaking…"
        : "Listening…";

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (hasText && !isStreaming && !dictating) onSend();
    }
  };

  // Figma 213:202356 input shell — one look for EVERY composer mode: solid
  // white, neutral-200 border, no shadow, and the blurred glow bleeding up
  // from the bottom edge. `isolate` makes the shell a stacking context so
  // the -z-10 glow layers paint above its white background.
  return (
    <div
      className={cn(
        "relative isolate flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white px-5 py-4",
        voiceUi ? "h-[110px] justify-between" : "gap-2",
      )}
    >
      {/* Blurred ellipses anchored just below the fold so only their soft
          tops rise into the card (Figma: top 96px of the 110px shell —
          bottom-anchored here so fluid-height modes keep the same edge).
          Blue corner glows show in every mode; the wide warm center wash is
          voice-mode only. Pure CSS stand-ins for the Figma 42px layer blur,
          clipped by the shell's overflow-hidden. */}
      <div className="-z-10 pointer-events-none absolute bottom-[-176px] left-[-98px] h-[190px] w-[213px] rounded-[50%] bg-primary-500/70 blur-[42px]" />
      <div className="-z-10 pointer-events-none absolute right-[-85px] bottom-[-176px] h-[190px] w-[213px] rounded-[50%] bg-primary-500 blur-[42px]" />
      {voiceUi && (
        <div className="-z-10 pointer-events-none absolute right-[31px] bottom-[-176px] left-[48px] h-[190px] rounded-[50%] bg-ai-glow-warm/40 blur-[42px]" />
      )}

      <div className="flex flex-col gap-1.5">
        {dictating && (
          <p className="paragraph-xs text-text-tertiary">{statusLabel}</p>
        )}
        {voiceUi ? (
          <p className="paragraph-sm text-text-tertiary">{statusLabel}</p>
        ) : (
          <Textarea
            rows={1}
            value={value}
            maxLength={maxChars}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={dictating ? "Speak now…" : placeholder}
            disabled={isStreaming}
            readOnly={dictating}
            className="min-h-9 resize-none border-0 bg-transparent p-1 paragraph-sm shadow-none placeholder:text-text-tertiary focus-visible:ring-0 disabled:opacity-60"
          />
        )}
      </div>

      <div className="flex items-end justify-between gap-2">
        <span
          className={cn(
            voiceUi
              ? "paragraph-sm text-[#bfbfbf]"
              : "paragraph-xs text-text-tertiary",
          )}
        >
          {value.length}/{maxChars}
        </span>

        <div className="flex items-center gap-2">
          {isStreaming ? (
            <IconButton
              label="Stop generating"
              onClick={() => onCancelTurn?.()}
            >
              <Square className="size-4 fill-current" />
            </IconButton>
          ) : dictating ? (
            <button
              type="button"
              aria-label="Stop listening"
              title="Stop"
              onClick={onStop}
              className="flex h-8 items-center gap-1.5 rounded-full bg-white px-3 text-text-primary shadow-sm transition-colors hover:bg-bg-weak-50"
            >
              <Mic className="size-4 shrink-0" strokeWidth={1.75} />
              <span className="paragraph-sm font-medium">Stop</span>
            </button>
          ) : voiceUi ? (
            <>
              {/* Static mic + waveform Stop pill (Figma 213:202362 Buttons). */}
              <Mic
                aria-hidden="true"
                className="size-6 shrink-0 text-text-secondary"
                strokeWidth={1.5}
              />
              <button
                type="button"
                aria-label="Stop voice mode"
                title="Stop"
                onClick={onStop}
                className="flex h-7 items-center gap-1.5 rounded-full bg-white pl-1.5 pr-2.5 text-text-primary transition-colors hover:bg-bg-weak-50"
              >
                {/* The Figma waveform glyph (213:202365) — not lucide AudioLines. */}
                <StopWaveIcon aria-hidden="true" className="size-7 shrink-0" />
                <span className="paragraph-sm">Stop</span>
              </button>
            </>
          ) : hasText ? (
            <>
              <IconButton label="Voice input" onClick={onStartDictation}>
                <Mic className="size-5" />
              </IconButton>
              <IconButton label="Send" onClick={onSend}>
                <Send className="size-5" />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton label="Voice input" onClick={onStartDictation}>
                <Mic className="size-5" />
              </IconButton>
              {showVoiceButton && (
                <IconButton label="Use voice mode" onClick={onStartVoice}>
                  <AudioLines className="size-5" />
                </IconButton>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
